/* Refresh cart presentation without navigating, opening a drawer or touching
   the design canvas. The server remains the source of truth in every tab. */
(function () {
  'use strict';
  if (window.RoboPixelCartSync) return;
  const nativeFetch = window.fetch.bind(window);
  const root = document.getElementById('cart-sync-script')?.dataset.rootUrl || '/';
  const signalKey = 'robopixel:cart-update';
  let revision = 0, mutations = 0, flight = null, timer = null, dirty = false;
  let channel;
  try { channel = new BroadcastChannel(signalKey); } catch (_) {}
  const busy = () => mutations > 0 || window.__cartRenderInProgress || window.RoboPixelPairJourney?.isAdding() || window.RoboPixelCoupons?.isBusy() ||
    !!document.querySelector('.cart__items--disabled');
  function notify() {
    try {
      if (channel) channel.postMessage('changed');
      else localStorage.setItem(signalKey, Date.now() + ':' + Math.random());
    } catch (_) { /* Focus and drawer-open refresh still work without storage. */ }
  }
  function schedule() {
    dirty = true;
    clearTimeout(timer);
    timer = setTimeout(() => { if (!document.hidden) refresh(); }, 100);
  }
  function changed() { ++revision; notify(); schedule(); }
  function remoteChange() { ++revision; schedule(); }
  if (channel) channel.onmessage = remoteChange;
  window.addEventListener('storage', event => { if (event.key === signalKey) remoteChange(); });

  function sectionTargets() {
    const targets = [
      { section: 'cart-drawer', selector: '#CartDrawer' },
      { section: 'cart-icon-bubble', selector: '#cart-icon-bubble', source: '.shopify-section' }
    ];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const node = document.getElementById(id);
      if (node?.dataset.id) targets.push({ section: node.dataset.id,
        selector: id === 'main-cart-items' ? 'cart-items' : '#' + id });
    }
    return targets;
  }
  function paint(sections, targets) {
    const updates = [];
    for (const target of targets) {
      const node = document.querySelector(target.selector);
      if (!node) continue;
      if (typeof sections[target.section] !== 'string') throw new Error('cart_section_missing');
      const doc = new DOMParser().parseFromString(sections[target.section], 'text/html');
      const source = doc.querySelector(target.source || target.selector);
      if (!source) throw new Error('cart_section_missing');
      updates.push({ node, source, doc, target });
    }
    // Validate every section before painting any: never mix old totals/new rows.
    const active = document.activeElement;
    const activeId = active?.id;
    const activeName = active?.getAttribute('name');
    const drawer = document.querySelector('cart-drawer');
    const drawerOpen = drawer?.classList.contains('active');
    const focusInDrawer = drawerOpen && drawer.contains(active);
    const focusInCart = !!active?.closest('cart-items, #main-cart-footer');
    for (const {node,source,doc,target} of updates) {
      node.innerHTML = source.innerHTML;
      if (target.selector === '#CartDrawer') {
        drawer?.classList.toggle('is-empty', !!doc.querySelector('cart-drawer.is-empty'));
        node.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer?.close());
      } else if (target.selector !== '#cart-icon-bubble') {
        node.classList.toggle('is-empty', source.classList.contains('is-empty'));
      }
    }
    if (focusInDrawer || focusInCart) {
      let replacement = activeId && document.getElementById(activeId);
      if (!replacement && activeName) replacement = Array.from(document.querySelectorAll('cart-drawer [name], cart-items [name]'))
        .find(node => node.getAttribute('name') === activeName);
      if (focusInDrawer) {
        const panel = drawer.querySelector('.drawer__inner');
        if (typeof trapFocus === 'function') trapFocus(panel, replacement || panel);
      } else replacement?.focus({ preventScroll: true });
    }
    window.RoboPixelPairJourney?.refresh().catch(() => {});
  }
  function refresh() {
    if (flight) { dirty = true; return flight; }
    if (busy()) { schedule(); return Promise.resolve(false); }
    clearTimeout(timer);
    dirty = false;
    const start = revision;
    const targets = sectionTargets();
    const url = new URL(root, window.location.origin);
    url.searchParams.set('sections', [...new Set(targets.map(item => item.section))].join(','));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    flight = (async () => {
      try {
        const response = await nativeFetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('cart_refresh_failed');
        const sections = await response.json();
        if (revision !== start || busy()) { dirty = true; return false; }
        paint(sections, targets);
        return true;
      } catch (_) { return false; /* Keep current UI; next open/focus retries. */ }
      finally { clearTimeout(timeout); flight = null; if (dirty) schedule(); }
    })();
    return flight;
  }

  // Track only same-origin Ajax cart writes. Invalidate reads as soon as a
  // mutation starts, not just after it finishes; preserve fetch's response.
  window.fetch = function (input, options) {
    let mutation = false;
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href);
      const method = (options?.method || input?.method || 'GET').toUpperCase();
      mutation = url.origin === window.location.origin && method !== 'GET' && method !== 'HEAD' &&
        /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    } catch (_) {}
    if (!mutation) return nativeFetch(input, options);
    ++revision; ++mutations;
    const settle = () => { --mutations; changed(); };
    try { return nativeFetch(input, options).finally(settle); }
    catch (error) { settle(); throw error; }
  };
  window.RoboPixelCartSync = { refresh, changed };
  window.addEventListener('robopixel:cart-changed', changed);
  document.addEventListener('robopixel:pixelback-returned', changed);
  window.addEventListener('focus', schedule);
  window.addEventListener('pageshow', event => { if (event.persisted) schedule(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  function subscribeCart() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, changed);
  }
  if (typeof subscribe === 'function') subscribeCart();
  else document.addEventListener('DOMContentLoaded', subscribeCart, { once: true });
})();
